import { db } from '@/data/firebase/config';
import { Campaign, CampaignFormData } from '@/domain/types/campaigns';
import {
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { useCallback, useState } from 'react';

const useCampaigns = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCampaigns = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const q = query(collection(db, 'campaigns'), orderBy('startDate', 'desc'));
      const snap = await getDocs(q);
      const data: Campaign[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<Campaign, 'id'>),
      }));
      setCampaigns(data);
    } catch (err) {
      setError('Error al cargar campañas');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createCampaign = useCallback(
    async (formData: CampaignFormData, createdBy: string) => {
      const payload = {
        name: formData.name,
        nameEn: formData.nameEn,
        hashtag: formData.hashtag.startsWith('#')
          ? formData.hashtag
          : `#${formData.hashtag}`,
        instructions: formData.instructions,
        instructionsEn: formData.instructionsEn,
        startDate: Timestamp.fromDate(new Date(formData.startDate + 'T00:00:00')),
        endDate: Timestamp.fromDate(new Date(formData.endDate + 'T23:59:59')),
        isActive: formData.isActive,
        createdAt: Timestamp.now(),
        createdBy,
      };
      await addDoc(collection(db, 'campaigns'), payload);
      await fetchCampaigns();
    },
    [fetchCampaigns],
  );

  const updateCampaign = useCallback(
    async (id: string, formData: Partial<CampaignFormData>) => {
      const payload: Partial<Campaign> = {};
      if (formData.name !== undefined) payload.name = formData.name;
      if (formData.nameEn !== undefined) payload.nameEn = formData.nameEn;
      if (formData.hashtag !== undefined)
        payload.hashtag = formData.hashtag.startsWith('#')
          ? formData.hashtag
          : `#${formData.hashtag}`;
      if (formData.instructions !== undefined)
        payload.instructions = formData.instructions;
      if (formData.instructionsEn !== undefined)
        payload.instructionsEn = formData.instructionsEn;
      if (formData.startDate !== undefined)
        payload.startDate = Timestamp.fromDate(new Date(formData.startDate + 'T00:00:00'));
      if (formData.endDate !== undefined)
        payload.endDate = Timestamp.fromDate(new Date(formData.endDate + 'T23:59:59'));
      if (formData.isActive !== undefined) payload.isActive = formData.isActive;
      await updateDoc(doc(db, 'campaigns', id), payload);
      await fetchCampaigns();
    },
    [fetchCampaigns],
  );

  const deleteCampaign = useCallback(
    async (id: string) => {
      await deleteDoc(doc(db, 'campaigns', id));
      await fetchCampaigns();
    },
    [fetchCampaigns],
  );

  const toggleActive = useCallback(
    async (id: string, isActive: boolean) => {
      await updateDoc(doc(db, 'campaigns', id), { isActive });
      await fetchCampaigns();
    },
    [fetchCampaigns],
  );

  const getActiveCampaign = useCallback(
    async (excludeId?: string): Promise<Campaign | null> => {
      const snap = await getDocs(
        query(collection(db, 'campaigns'), where('isActive', '==', true)),
      );
      const active = snap.docs.find((d) => d.id !== excludeId);
      if (!active) return null;
      return { id: active.id, ...(active.data() as Omit<Campaign, 'id'>) };
    },
    [],
  );

  return {
    campaigns,
    isLoading,
    error,
    fetchCampaigns,
    createCampaign,
    updateCampaign,
    deleteCampaign,
    toggleActive,
    getActiveCampaign,
  };
};

export default useCampaigns;
